import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { createAuthAPI } from '../../utils/api';
import { toast } from 'sonner';
import { Plus, Trash2, Edit } from 'lucide-react';

const ExerciseManagement = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  
  const [videoForm, setVideoForm] = useState({
    titulo: '',
    url: '',
    duracion_minutos: '',
    orden: ''
  });

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const api = createAuthAPI();
      const response = await api.get('/admin/exercise-videos');
      setVideos(response.data);
    } catch (error) {
      toast.error('Error al cargar videos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const api = createAuthAPI();
      const data = {
        ...videoForm,
        duracion_minutos: parseInt(videoForm.duracion_minutos),
        orden: parseInt(videoForm.orden)
      };
      
      if (editingVideo) {
        await api.put(`/admin/exercise-videos/${editingVideo.id}`, data);
        toast.success('Video actualizado');
      } else {
        await api.post('/admin/exercise-videos', data);
        toast.success('Video añadido exitosamente');
      }
      
      setShowAddDialog(false);
      setEditingVideo(null);
      loadVideos();
      setVideoForm({ titulo: '', url: '', duracion_minutos: '', orden: '' });
    } catch (error) {
      toast.error('Error al guardar video');
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setVideoForm({
      titulo: video.titulo,
      url: video.url,
      duracion_minutos: video.duracion_minutos.toString(),
      orden: video.orden.toString()
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('¿Estás seguro de eliminar este video?')) return;
    
    try {
      const api = createAuthAPI();
      await api.delete(`/admin/exercise-videos/${videoId}`);
      toast.success('Video eliminado');
      loadVideos();
    } catch (error) {
      toast.error('Error al eliminar video');
    }
  };

  const closeDialog = () => {
    setShowAddDialog(false);
    setEditingVideo(null);
    setVideoForm({ titulo: '', url: '', duracion_minutos: '', orden: '' });
  };

  if (loading) {
    return <div>Cargando videos...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-green-800">Gestión de Videos de Ejercicio</h2>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="pharmacy-button text-white" data-testid="add-video-button">
              <Plus className="h-4 w-4 mr-2" />
              Añadir Video
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-green-800 mb-4">
                {editingVideo ? 'Editar Video de Ejercicio' : 'Nuevo Video de Ejercicio'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="video-form">
              <div className="space-y-2">
                <Label htmlFor="titulo" className="text-base font-semibold text-gray-700">Título del Video *</Label>
                <Input
                  id="titulo"
                  value={videoForm.titulo}
                  onChange={(e) => setVideoForm({...videoForm, titulo: e.target.value})}
                  required
                  className="text-base py-6 border-2 border-gray-300 focus:border-green-500"
                  placeholder="Ej: Ejercicios de 5 minutos para principiantes"
                  data-testid="titulo-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="url" className="text-base font-semibold text-gray-700">URL del Video *</Label>
                <Input
                  id="url"
                  type="url"
                  value={videoForm.url}
                  onChange={(e) => setVideoForm({...videoForm, url: e.target.value})}
                  placeholder="https://youtube.com/..."
                  required
                  className="text-base py-6 border-2 border-gray-300 focus:border-green-500"
                  data-testid="url-input"
                />
                <p className="text-sm text-green-700 bg-green-50 p-3 rounded-lg mt-2">
                  ℹ️ Puedes usar links de YouTube, TikTok o Instagram
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="duracion_minutos" className="text-base font-semibold text-gray-700">Duración (minutos) *</Label>
                  <Input
                    id="duracion_minutos"
                    type="number"
                    value={videoForm.duracion_minutos}
                    onChange={(e) => setVideoForm({...videoForm, duracion_minutos: e.target.value})}
                    required
                    className="text-lg py-6 border-2 border-gray-300 focus:border-green-500"
                    placeholder="Ej: 5"
                    data-testid="duracion-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="orden" className="text-base font-semibold text-gray-700">Orden *</Label>
                  <Input
                    id="orden"
                    type="number"
                    value={videoForm.orden}
                    onChange={(e) => setVideoForm({...videoForm, orden: e.target.value})}
                    required
                    className="text-lg py-6 border-2 border-gray-300 focus:border-green-500"
                    placeholder="Ej: 1"
                    data-testid="orden-input"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 pharmacy-button text-white py-6 text-lg font-semibold" data-testid="submit-video-button">
                  {editingVideo ? '✓ Actualizar Video' : '✓ Añadir Video'}
                </Button>
                <Button type="button" variant="outline" onClick={closeDialog} className="px-8 py-6 text-base">
                  Cancelar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {videos.map((video) => (
          <Card key={video.id} className="pharmacy-card" data-testid={`video-card-${video.id}`}>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-green-800">{video.titulo}</h3>
                  <p className="text-sm text-gray-600 mt-1">Duración: {video.duracion_minutos} minutos</p>
                  <p className="text-sm text-gray-600">Orden: {video.orden}</p>
                  <a 
                    href={video.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline mt-2 inline-block"
                  >
                    Ver video
                  </a>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleEdit(video)}
                    variant="outline"
                    size="sm"
                    data-testid={`edit-video-${video.id}`}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => handleDelete(video.id)}
                    variant="destructive"
                    size="sm"
                    data-testid={`delete-video-${video.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {videos.length === 0 && (
          <p className="text-center text-gray-500 py-8">No hay videos aún. Añade el primero.</p>
        )}
      </div>
    </div>
  );
};

export default ExerciseManagement;